<template>
  <div class="min-h-screen bg-transparent">
    <div ref="panelRef" class="flex flex-col gap-1.5 w-full p-4 rounded-3xl bg-white">
      <div class="flex items-center gap-1">
        <img width="16" height="16" :src="logo" class="size-4" />
        <span class="text-l-medium text-text-active">{{ t('tray.title') }}</span>
      </div>
      <div class="flex flex-col gap-1.5">
        <div
          v-for="device in devices"
          :key="device.id"
          class="p-2 flex flex-col gap-2 bg-neutral-light-default1 rounded-lg"
        >
          <div class="flex items-center gap-1.5">
            <Icon :icon="device.icon" class="size-6" />
            <span class="text-m-medium text-text-active">{{ device.name }}</span>
          </div>
          <div class="flex items-center gap-1.5">
            <Icon :icon="getConnectionIcon(device.connectionType)" class="size-6" />
            <span v-if="device.batteryStatus != 0" class="text-m-medium text-text-active">
              {{ device.battery }}%
            </span>
            <Icon
              :icon="getBatteryIcon(device.battery, device.batteryStatus)"
              class="size-6"
              :class="getBatteryIconClass(device.battery, device.batteryStatus)"
            />
            <div class="flex-1"></div>
            <Tooltip side="bottom" align="end">
              <div>
                <Switch
                  :model-value="activeDeviceId === device.id"
                  @update:model-value="(val: boolean) => handleSwitch(device, val)"
                />
              </div>
              <template #content>
                <div
                  class="w-34 text-s-medium text-white-black p-1 bg-floating-tooltip-container rounded-m"
                >
                  {{ tipsText(device) }}
                </div>
              </template>
            </Tooltip>
          </div>
          <div class="ml-1 text-m-medium text-text-active">
            {{ device.currentConfigName }}
          </div>
        </div>
      </div>
      <Button
        color="text-b-b"
        size="sm"
        class="w-full rounded-lg justify-start"
        @click="handleResetSize"
      >
        {{ t('tray.resetSize') }}
      </Button>
      <Button
        v-if="hasNewVersion"
        color="blue1"
        size="sm"
        class="w-full rounded-lg justify-start"
        @click="handleUpdate"
      >
        {{ t('tray.update') }}
      </Button>
      <Button color="blue2" size="sm" class="w-full rounded-lg justify-start" @click="handleQuit">
        {{ t('tray.quit') }}
      </Button>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
  import { Button, Icon, Switch, Tooltip } from '@rapoo/element'
  import { storeToRefs } from 'pinia'
  import { useTrayStore, useUpdateStore, useLocaleStore } from '@/stores'
  import { ConnectionType } from '@rapoo/device-sdk'
  import logo from '@/assets/images/header/user/avatar-loggedIn.png'

  const localeStore = useLocaleStore()
  const { t } = localeStore
  localeStore.initLocale()

  // 订阅主进程推送的语言变更（通过 IPC），实时同步界面
  let unsubscribeLocale: (() => void) | undefined = undefined

  const ROOT_VERTICAL_PADDING = 16

  const panelRef = ref<HTMLElement | null>(null)
  const trayStore = useTrayStore()
  const { deviceState } = storeToRefs(trayStore)

  const updateStore = useUpdateStore()
  const { hasNewVersion } = storeToRefs(updateStore)

  let resizeObserver: ResizeObserver | null = null
  let lastReportedHeight = 0

  const devices = computed(() => deviceState.value)

  const activeDeviceId = ref<string | null>(null)
  let lastIconType = ''

  const getBatteryIconType = (battery: number, batteryStatus: number): string => {
    if (batteryStatus !== 1) return 'battery-charging'
    if (battery < 30) return 'battery-0-29'
    if (battery < 50) return 'battery-30-49'
    if (battery < 80) return 'battery-50-79'
    return 'battery-80-100'
  }

  const handleSwitch = (
    device: { id: string; battery: number; batteryStatus: number },
    checked: boolean,
  ) => {
    if (checked) {
      activeDeviceId.value = device.id
      const iconType = getBatteryIconType(device.battery, device.batteryStatus)
      lastIconType = iconType
      window.electronAPI?.setTrayBatteryIcon(iconType)
    } else {
      activeDeviceId.value = null
      lastIconType = ''
      window.electronAPI?.setTrayBatteryIcon('default')
    }
  }

  const tipsText = (device: { id: string; battery: number; batteryStatus: number }) => {
    if (activeDeviceId.value === device.id) {
      return t('tray.tips.disable')
    }
    return t('tray.tips.enable')
  }

  watch(
    devices,
    (list) => {
      if (!activeDeviceId.value) return

      const active = list.find((device) => device.id === activeDeviceId.value)
      if (!active) {
        activeDeviceId.value = null
        lastIconType = ''
        window.electronAPI?.setTrayBatteryIcon('default')
        return
      }

      const iconType = getBatteryIconType(active.battery, active.batteryStatus)
      if (iconType !== lastIconType) {
        lastIconType = iconType
        window.electronAPI?.setTrayBatteryIcon(iconType)
      }
    },
    { deep: true },
  )

  const getBatteryIcon = (battery: number, batteryStatus: number) => {
    if (batteryStatus !== 1) return 'Hardware-Fill-Charging'
    if (battery < 30) return 'Hardware-Fill-0-29Battery'
    if (battery < 50) return 'Hardware-Fill-30-49Battery'
    if (battery < 80) return 'Hardware-Fill-50-79Battery'
    return 'Hardware-Fill-80-100Battery'
  }

  const getBatteryIconClass = (battery: number, batteryStatus: number) => {
    if (batteryStatus !== 1) return 'text-green-active'
    if (battery < 30) return 'text-red-active'
    if (battery < 50) return 'text-orange-active'
    return 'text-green-active'
  }

  const syncTrayHeight = async () => {
    await nextTick()

    const panelElement = panelRef.value
    if (!panelElement) return

    const nextHeight =
      Math.ceil(panelElement.getBoundingClientRect().height) + ROOT_VERTICAL_PADDING
    if (!nextHeight || nextHeight === lastReportedHeight) return

    lastReportedHeight = nextHeight
    await window.electronAPI?.resizeTrayPanel(nextHeight)
  }

  onMounted(async () => {
    await trayStore.initTrayState()
    void syncTrayHeight()

    // 订阅主进程语言变更推送，实时切换 locale
    unsubscribeLocale = localeStore.listenLocaleChange()

    if ('ResizeObserver' in window && panelRef.value) {
      resizeObserver = new ResizeObserver(() => {
        void syncTrayHeight()
      })
      resizeObserver.observe(panelRef.value)
    }
  })

  onBeforeUnmount(() => {
    trayStore.disposeTrayState()
    unsubscribeLocale?.()
    resizeObserver?.disconnect()
    resizeObserver = null
  })

  const getConnectionIcon = (type: ConnectionType) => {
    switch (type) {
      case ConnectionType.Wired:
        return 'Hardware-Wireframe-Wired'
      case ConnectionType.Wireless:
        return 'Hardware-Wireframe-Wireless'
      default:
        return 'Hardware-Wireframe-Bluetooth'
    }
  }

  const handleResetSize = async () => {
    await window.electronAPI?.openMainWindow()
    await window.electronAPI?.resetWindowSize()
    await window.electronAPI?.closeTrayPanel()
  }

  const handleUpdate = async () => {
    await window.electronAPI?.openMainWindow(
      '/headerConsole?com=settings&sub=about&action=checkUpdate',
    )
    await window.electronAPI?.closeTrayPanel()
  }

  const handleQuit = () => {
    window.electronAPI?.appQuit()
  }
</script>

<style scoped></style>
